// Homework practice (LB-364, drawings signed off 25 Sep:
// https://claude.ai/artifact/PpyDA5QReqmCFyv4g4UzoP).
//
// A parent sets a PRACTICE LIST from what school sent home (spellings, English vocabulary,
// a language vocabulary sheet) and the child practises it in Learn & Earn until the words
// stick. It sits beside Learning Coach and never touches it: nothing here reads or writes
// `learningContent[id].adaptive`, and no go ever reaches the engine.
//
// Reframed with Alan on 24/25 Sep: this is active practising, not testing. A test is one
// event with a score; practice is a loop. So there is no pass mark and no timer. Every go
// pays Oinks for right answers; an optional token reward pays ONCE, when every word on the
// list is known. A word is KNOWN once it has been answered right on two different days,
// the rule vocabulary already uses.
//
// State: learningContent[childId].homework = { lists: [list, ...] }. Every list is minted
// with a short PUBLIC CODE at creation and everything keys off it — the practice round,
// the progress, the history rows. Phase one only mints and uses it; letting another family
// redeem it is LB-374, so sharing later needs no migration.
//
// list = {
//   code, type ("spelling" | "english" | "language"), name, lang, direction
//   ("en2lang" | "lang2en" | "both"), pairs: [{ id, a, b }], oinksOn, finishTokens,
//   until (YYYY-MM-DD, the last day it is on the child's grid), createdAt, adultId,
//   adultName, progress: { [pairId]: { days: [YYYY-MM-DD], knownAt, lastWrong, right,
//   wrong } }, goes, oinksEarned, lastGoAt, finishedAt
// }
// For a spellings list `a` is the word and `b` is empty. For English vocabulary `a` is the
// word and `b` its meaning. For a language list `a` is English and `b` the other language.

import { premiumUnlocked } from "./premium.js";

// Homework practice is a Premium feature (Alan, 2 Oct: "Homework should be behind premium
// flag"). THE one question every homework site asks, so homework has a single place to change,
// as premium.js is the single place for the app. Never read PREMIUM_FREE_DURING_PREVIEW or
// settings.premium in a homework file; ask this.
//
// Today it is true for everyone (the Preview), so nothing is withheld. When it is false:
// nothing NEW starts or spreads (Set homework, activating a draft or a shelved one, editing,
// sharing, giving to a sibling, adding a shared one, approving a child's photo, a child's own
// Add new homework), and what is already live keeps running to its date, Oinks and finish
// tokens included. Archiving, deleting, declining and viewing are never gated.
//
// The locked state shows ONE neutral line, homework.premium_locked. It is a PLACEHOLDER, to be
// replaced when payment exists: no button, no price, no upgrade. The flag must not be flipped
// before then (Alan: "I won't enable the premium validation until payment is available").
export function homeworkUnlocked(settings) {
  return premiumUnlocked(settings);
}

export const HW_TYPES = ["spelling", "english", "language"];
export const HW_DIRECTIONS = ["en2lang", "lang2en", "both"];
export const HW_MAX_LIVE = 5; // on the go per child at once (Alan, 25 Sep: the grid is the limit; 1 Oct: raised from 3 to 5)
export const HW_KNOWN_DAYS = 2; // right on this many different days = known
export const HW_FINISHED_KEEP = 5; // superseded by HW_SHELF_KEEP; kept so nothing breaks on it
// LB-381 (Alan, 27 Sep): a child photographs their own sheet and sends the words to a parent,
// who approves, edits or rejects. It is an ordinary entry in `state.requests`, which is what
// gives it the Requests badge, the parent's approve/reject, the child's history row and the
// push worker/lib/notifications.js already sends when any request is resolved — none of which
// had to be built. The payload is on `extra`: the pairs as read, and how many rows were dropped.
//
// Its cap is its OWN, not the five that govern earning requests (his call): a sheet arriving is
// not the same kind of ask as "can I have three tokens", and one should not crowd out the other.
export const HW_MAX_SUBMISSIONS = 3;
export const HW_SUBMISSION_TYPE = "homework_sheet";

export function pendingSubmissions(requests, childId) {
  return (requests || []).filter((r) => r && r.requestType === HW_SUBMISSION_TYPE && r.status === "pending" && (!childId || r.childId === childId));
}

export function canSubmitHomework(requests, childId) {
  return pendingSubmissions(requests, childId).length < HW_MAX_SUBMISSIONS;
}

// The child's most recent sheet, whatever became of it. What HomeworkSubmit shows above the
// camera: a sheet a parent sent back stays on the card, with the parent's line, until the child
// sends the next one (Alan, staging, 28 Sep: a rejection "didn't show up to the kid").
export function latestSubmission(requests, childId) {
  const mine = (requests || []).filter((r) => r && r.requestType === HW_SUBMISSION_TYPE && (!childId || r.childId === childId));
  if (!mine.length) return null;
  return mine.slice().sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || "")))[0];
}

// LB-382, second cut (Alan, staging, 28 Sep). A list arriving by link used to take over the
// Home tab with no badge, no request and nothing that survived closing the app. It is now an
// ordinary REQUEST, like a sheet the child sent: it sits in Requests with the badge, it is
// checked on the same review, and it waits there until a parent decides. It has no child until
// the parent picks one on the way in, which is why childId starts null.
export const HW_SHARED_TYPE = "homework_shared";

export function isHomeworkRequest(r) {
  return !!r && (r.requestType === HW_SUBMISSION_TYPE || r.requestType === HW_SHARED_TYPE);
}

export function findPendingShared(requests, sharedCode) {
  return (requests || []).find((r) => r && r.requestType === HW_SHARED_TYPE && r.status === "pending" && r.sharedCode === sharedCode) || null;
}

// The request a shared snapshot becomes. Words only, and the snapshot already carries nothing
// about the family that shared it.
export function sharedListRequest(list, { id, now }) {
  return {
    id, childId: null, requestType: HW_SHARED_TYPE,
    title: String(list.name || "").trim() || "Homework",
    requestedAmount: 0, goalId: null, status: "pending", timestamp: now,
    sharedCode: list.code || null,
    listType: list.type === "language" || list.type === "english" ? list.type : "spelling",
    lang: String(list.lang || ""), direction: ["en2lang", "lang2en", "both"].includes(list.direction) ? list.direction : "en2lang", schoolYear: String(list.schoolYear || ""),
    pairs: (list.pairs || []).map((p) => ({ a: String(p.a || ""), b: String(p.b || "") })).filter((p) => p.a),
    droppedCount: 0,
  };
}

// What the child is allowed to send. A row the rude filter catches is dropped HERE, before the
// child ever sees the words (his call, 27 Sep) — the whole point of the filter is that a child
// is not shown one, and showing it in order to refuse it defeats that. The count of what went
// is carried so the parent can be told the sheet had more rows than the list does.
export function cleanSubmission(pairs, isRude) {
  const kept = []; let dropped = 0;
  for (const p of pairs || []) {
    const a = String(p.a || "").trim(); const b = String(p.b || "").trim();
    if (!a) continue;
    if (isRude && (isRude(a) || isRude(b))) { dropped += 1; continue; }
    kept.push({ a, b });
  }
  return { pairs: kept, dropped };
}

export const HW_MAX_DRAFTS = 3;    // LB-377: drafts sit OUTSIDE the live limit, up to this many
export const HW_SHELF_KEEP = 10;   // LB-377 (Alan, 27 Sep): finished AND archived lists kept
export const HW_MAX_PAIRS = 60;
export const HW_MIN_CHOICES = 3; // an English-vocabulary list needs this many pairs for choices
export const HW_CODE_PREFIX = "HW-";
// No 0/O/1/I/L: a code is read out loud and typed by someone else one day (LB-374).
const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const CODE_LENGTH = 6;

function randomInt(n) {
  try {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const buf = new Uint32Array(1);
      crypto.getRandomValues(buf);
      return buf[0] % n;
    }
  } catch { /* fall through */ }
  return Math.floor(Math.random() * n);
}

// HW-XXXXXX, unique against the codes already in the household. Six characters of a 31
// letter alphabet is ~887 million codes, which is enough headroom for LB-374 to keep the
// same shape when codes become redeemable across households.
export function mintHomeworkCode(existing = [], rnd = randomInt) {
  const taken = new Set((existing || []).map((c) => String(c).toUpperCase()));
  for (let attempt = 0; attempt < 50; attempt++) {
    let s = "";
    for (let i = 0; i < CODE_LENGTH; i++) s += CODE_ALPHABET[rnd(CODE_ALPHABET.length)];
    const code = HW_CODE_PREFIX + s;
    if (!taken.has(code)) return code;
  }
  return HW_CODE_PREFIX + String(Date.now()).slice(-CODE_LENGTH);
}

export function isHomeworkCode(s) {
  return typeof s === "string" && new RegExp(`^${HW_CODE_PREFIX}[${CODE_ALPHABET}]{${CODE_LENGTH}}$`).test(s);
}

// ---- parsing what the parent typed or pasted ------------------------------------------
//
// One entry per line. A pair is split on the first of: tab, " - ", " – ", " = ", ":", ",",
// "|". Leading numbering ("1.", "3)", "(4)") and bullets are dropped, so a sheet pasted with
// its numbers still reads. Trailing full stops go too. Lines that yield nothing are returned
// in `dropped` so the review screen can say what was ignored, and `droppedWhy` (same length,
// same order) says why: "empty" (nothing left once numbering and bullets go) or "nopair" (a
// list of pairs, and the line has only one half). Alan, 27 Sep: "6 lines ignored" with no
// what or why was a dead end.
const SEPARATORS = [/\t+/, /\s+[-–—]\s+/, /\s*=\s*/, /\s*:\s*/, /\s*\|\s*/, /\s*,\s*/];

function cleanCell(s) {
  return String(s || "")
    .replace(/^[\s\u2022\u2023\u25E6\u2043\u2219*·-]+/, "") // bullets
    .replace(/^\(?\d{1,3}[.)]\s*/, "") // 1.  2)  (3)
    .replace(/\s+/g, " ")
    .replace(/[.\s]+$/g, "")
    .trim();
}

export function parseHomeworkText(text, type) {
  const pairs = [];
  const dropped = [];
  const droppedWhy = [];
  const lines = String(text || "").split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (type === "spelling") {
      // A word with its "as in" (Alan, 5 Oct): "write - as in to put words on paper", or a tab
      // between them. The line is one word then, however many commas the "as in" has.
      const withHint = line.match(/^([^,;\t]+?)\s*(?:\t+|\s[-–—=]\s|:\s*(?=as in\b))\s*(\S.*)$/i);
      if (withHint && cleanCell(withHint[1]) && !/\s/.test(cleanCell(withHint[1]))) { pairs.push({ a: cleanCell(withHint[1]), b: withHint[2].trim().slice(0, 160) }); continue; }
      // Spellings: one word per line, or several on a line separated by commas.
      const words = line.split(/[,;]/).map(cleanCell).filter(Boolean);
      if (!words.length) { dropped.push(raw); droppedWhy.push("empty"); continue; }
      for (const w of words) pairs.push({ a: w, b: "" });
      continue;
    }
    let a = null; let b = null;
    for (const sep of SEPARATORS) {
      const m = line.split(sep);
      if (m.length >= 2 && cleanCell(m[0]) && cleanCell(m.slice(1).join(" "))) { a = cleanCell(m[0]); b = cleanCell(m.slice(1).join(" ")); break; }
    }
    if (!a || !b) { dropped.push(raw); droppedWhy.push(cleanCell(line) ? "nopair" : "empty"); continue; }
    pairs.push({ a, b });
  }
  return { pairs: pairs.slice(0, HW_MAX_PAIRS), dropped, droppedWhy, truncated: pairs.length > HW_MAX_PAIRS };
}

// ---- marking ---------------------------------------------------------------------------
//
// Accents are never marked wrong (Alan, 25 Sep: the on-screen keyboard cannot type them).
// Case, extra spaces and trailing punctuation are ignored too. For a vocabulary answer the
// leading article is optional either way: "dog" for "the dog" is right, and so is "the dog"
// for "dog". The accented form is still SHOWN in the feedback so the child sees it.
// Hyphens (Alan, 28 Sep: "slave-girl"): the kid keyboard has no hyphen key, so a hyphen in
// the answer is read as a space — "slave girl" is right — and answersMatchLoose also accepts
// the words run together ("slavegirl"), which is how a child who knows the word may well
// write it.
// Invisible characters (Alan, 28 Sep: "amant" typed, "Not quite, it was amant"): a word that
// came off a photographed sheet or a paste can carry a zero-width space, soft hyphen or
// direction mark. It draws exactly like the plain word and never equals what was typed, so
// every format character (Unicode Cf) is dropped before comparing. Done here, at marking,
// so lists already saved are fixed too.
export function normaliseAnswer(s) {
  return String(s || "")
    .replace(/\p{Cf}/gu, "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\u2019']/g, "'")
    .replace(/[\u2010-\u2014\u2212-]/g, " ")
    .replace(/[.,;:!?"“”()]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const ARTICLES = /^(the|a|an|to|le|la|les|un|une|el|los|las|der|die|das|il|lo|gli|i)\s+/;

// LB-377 follow-up (Alan, 27 Sep, from the Latin sheets he photographed). A grammar table writes
// its English for READING, not for typing — "He/she/it loves", "You (sg.) love", "live, inhabit"
// — and the child's keyboard is letters and a space, with no slash and no brackets. The old rule
// split the whole answer on "/" and produced "he", "she", "it loves", so a child who typed
// "he loves" was told they were wrong, and "You (sg.) love" demanded they type "sg", which is a
// reading aid rather than a word. Every form below is generated from what the sheet says:
//
//   He/she/it loves  →  he loves · she loves · it loves · he/she/it loves
//   You (sg.) love   →  you love · you sg love
//   live, inhabit    →  live · inhabit · live inhabit
//
// The slash is expanded INSIDE its word and the rest of the phrase goes with each branch, which
// is what keeps the pronoun compulsory: "loves" on its own is never generated and stays wrong,
// because the pronoun is half of what the table teaches.
export function answerForms(answer) {
  const raw = String(answer || "");
  const out = new Set();
  const addPhrase = (phrase) => {
    // every combination of the slash-separated branches, each keeping the words around it.
    // A sheet may space the slash out ("dog / hound"); tightening it first keeps the branches
    // attached to their word instead of leaving a bare "/" as a token of its own.
    const tokens = phrase.replace(/\s*\/\s*/g, "/").split(/\s+/).filter(Boolean);
    let combos = [[]];
    for (const tok of tokens) {
      const branches = tok.includes("/") ? tok.split("/").filter(Boolean) : [tok];
      combos = combos.flatMap((c) => branches.map((b) => [...c, b]));
      if (combos.length > 24) combos = combos.slice(0, 24); // a pathological cell cannot run away
    }
    for (const c of combos) { const n = normaliseAnswer(c.join(" ")); if (n) out.add(n); }
  };
  // with the bracketed aside, and without it: "You (sg.) love" is also just "You love"
  for (const variant of [raw, raw.replace(/\s*\([^)]*\)\s*/g, " ")]) {
    const whole = variant.trim();
    if (!whole) continue;
    const asWritten = normaliseAnswer(whole);
    if (asWritten) out.add(asWritten); // typed exactly as the sheet has it, slashes and all
    addPhrase(whole);
    // "live, inhabit" — a gloss listing two meanings accepts either, or both as written
    for (const branch of whole.split(/\s*[,;]\s*|\s+or\s+/i)) if (branch.trim()) addPhrase(branch.trim());
  }
  return [...out];
}

// The pronoun I misread by the photo reader (Alan, 3 Oct: "Not quite, it was I attack" for
// "i attack"; the list said "l attack", which the app's font draws exactly like "I attack"). A
// lone l or | in a saved answer is read as I. A lone l is never an English word, and doing it
// here, at marking, fixes lists already saved, as the invisible-character rule above does.
const lonePronoun = (form) => form.replace(/(^| )[l|](?= |$)/g, "$1i");

export function answersMatchLoose(given, answer) {
  const g = normaliseAnswer(given);
  if (!g || !normaliseAnswer(answer)) return false;
  const ga = g.replace(ARTICLES, "");
  const joined = (x) => x.replace(/ /g, "");
  return answerForms(answer).flatMap((f) => [f, lonePronoun(f)]).some((alt) => alt === g || alt.replace(ARTICLES, "") === ga || (/-/.test(String(answer)) && joined(alt) === joined(g)));
}

// Alan, 26 Sep: if the language is in the English column, say so. A small set of the most
// common English words is enough to tell which column is English on a real sheet; a column
// with no hits on either side is left alone (proper nouns, single rare words).
const ENGLISH_HINTS = new Set(("the a an to of and in is it you that he was for on are with as i his they be at one have this from or had by " +
  "not word but what some we can out other were all there when up use your how said each she which do their time if will way about many then them " +
  "would write like so these her long make thing see him two has look more day could go come did my sound no most number who over know water than " +
  "first people down side been now find any new work part take get place made live where after back little only round man year came show every " +
  "good me give our under name very through just form much great think say help low line before turn cause same mean differ move right boy old too " +
  "does tell sentence set three want air well also play small end put home read hand port large spell add even land here must big high such follow " +
  "act why ask men change went light kind off need house picture try us again animal point mother world near build self earth father head stand own " +
  "page should country found answer school grow study still learn plant cover food sun four thought let keep eye never last door between city tree " +
  "cross since hard start might story saw far sea draw left late run while press close night real life few stop open seem together next white " +
  "children begin got walk example ease paper often always music those both mark book letter until mile river car feet care second group carry took " +
  "rain eat room friend began idea fish mountain north once base hear horse cut sure watch colour face wood main enough plain girl usual young ready " +
  "above ever red list though feel talk bird soon body dog family direct pose leave song measure state product black short numeral class wind question " +
  "happen complete ship area half rock order fire south problem piece told knew pass farm top whole king size heard best hour better true during " +
  "hundred am remember step early hold west ground interest reach fast five sing listen six table travel less morning ten simple several vowel toward " +
  "war lay against pattern slow centre love person money serve appear road map science rule govern pull cold notice voice fall power town fine " +
  "certain fly unit lead cry dark machine note wait plan figure star box noun field rest correct able pound done beauty drive stood contain front " +
  "teach week final gave green oh quick develop sleep warm free minute strong special mind behind clear tail produce fact street inch lot nothing " +
  "course stay wheel full force blue object decide surface deep moon island foot yet busy test record boat common gold possible plane age dry wonder " +
  "laugh thousand ago ran check game shape yes hot miss brought heat snow bed bring sit perhaps fill east weight language among cat bread cheese " +
  "apple farmer sailor queen girl boy dog water milk house window brother sister mother father").split(/\s+/));

function englishScore(cells) {
  let hits = 0; let words = 0;
  for (const c of cells) for (const w of normaliseAnswer(c).split(" ")) { if (!w) continue; words += 1; if (ENGLISH_HINTS.has(w)) hits += 1; }
  return words ? hits / words : 0;
}

// True when column B reads as the English one. Only confident calls: B must clearly beat A.
export function looksSwapped(pairs) {
  const rows = (pairs || []).filter((p) => p && p.a && p.b);
  if (rows.length < 2) return false;
  const a = englishScore(rows.map((p) => p.a));
  const b = englishScore(rows.map((p) => p.b));
  return b > 0.3 && b > a * 2;
}

// Alan, 26 Sep: guess the language from the words, so the field is prefilled. Small,
// high-precision hints per language; no guess when nothing matches. The parent can change it.
export const HW_LANGUAGES = ["French", "Spanish", "German", "Latin", "Italian"];
const LANG_HINTS = {
  French: new Set("le la les un une des du de et est je tu il elle nous vous ils elles chien chat maison pain fromage pomme eau merci bonjour oui non avec pour dans sur mon ma mes".split(" ")),
  Spanish: new Set("el la los las un una unos unas y es yo tú él ella nosotros perro gato casa pan queso manzana agua gracias hola sí no con para en mi".split(" ")),
  German: new Set("der die das ein eine und ist ich du er sie wir ihr hund katze haus brot käse apfel wasser danke hallo ja nein mit für in mein".split(" ")),
  Italian: new Set("il lo la i gli le un una uno e è io tu lui lei noi cane gatto casa pane formaggio mela acqua grazie ciao sì con per in mio".split(" ")),
  Latin: new Set("et est sum es sunt canis puella puer agricola aqua insula regina nauta rex femina vir amicus servus villa terra deus dea magnus bonus parvus".split(" ")),
};
const LATIN_ENDINGS = /(us|um|ae|is|orum|arum|ibus)$/;

export function guessLanguage(pairs) {
  const cells = (pairs || []).map((p) => p && p.b).filter(Boolean);
  if (cells.length < 2) return "";
  const scores = Object.fromEntries(HW_LANGUAGES.map((l) => [l, 0]));
  for (const c of cells) {
    const norm = String(c).toLowerCase().replace(/[.,;:!?"“”()]/g, "").trim();
    for (const w of norm.split(/\s+/)) {
      if (!w) continue;
      for (const l of HW_LANGUAGES) if (LANG_HINTS[l].has(w)) scores[l] += 2;
      if (LATIN_ENDINGS.test(w) && !/[éèêàçñüößìò]/.test(w)) scores.Latin += 1;
    }
    if (/[éèêàçœ]/.test(norm)) scores.French += 1;
    if (/[ñ¿¡]/.test(norm)) scores.Spanish += 2;
    if (/[äöüß]/.test(norm)) scores.German += 2;
  }
  const best = HW_LANGUAGES.reduce((a, l) => (scores[l] > scores[a] ? l : a), HW_LANGUAGES[0]);
  const second = Math.max(...HW_LANGUAGES.filter((l) => l !== best).map((l) => scores[l]));
  return scores[best] >= 2 && scores[best] > second ? best : "";
}

// ---- lists ------------------------------------------------------------------------------

// A spelling's "as in" always reads "as in …" (Alan, 6 Oct). The word bank records only lines
// that start that way (worker/lib/tts-wordbank.js), so a parent's "a big fight" under "war" was
// read by the device voice while every other line had the recorded one. Saved as "as in a big
// fight": shown and spoken like that, and recorded. Applied wherever a list is saved, so no screen
// can miss it; a language or vocabulary list's second side is its meaning and is left alone.
export function asInLine(text) {
  const t = String(text || "").trim();
  if (!t) return "";
  return /^as in\b/i.test(t) ? t : `as in ${t}`.slice(0, 160);
}
const sideB = (type, b) => (type === "spelling" ? asInLine(b) : String(b || "").trim());

export function newHomeworkList({ code, type, name, lang = "", direction = "en2lang", pairs, oinksOn = true, finishTokens = 0, until, createdAt, actor = null, makeId, status = null }) {
  const mk = typeof makeId === "function" ? makeId : (i) => `${code}-${i + 1}`;
  const list = {
    code,
    type: HW_TYPES.includes(type) ? type : "spelling",
    name: String(name || "").trim().slice(0, 60),
    lang: type === "language" ? String(lang || "").trim().slice(0, 30) : "",
    direction: type === "language" && HW_DIRECTIONS.includes(direction) ? direction : "en2lang",
    pairs: (pairs || []).slice(0, HW_MAX_PAIRS).map((p, i) => ({ id: mk(i), a: String(p.a || "").trim(), b: sideB(HW_TYPES.includes(type) ? type : "spelling", p.b) })).filter((p) => p.a),
    oinksOn: !!oinksOn,
    finishTokens: Math.max(0, Math.floor(Number(finishTokens) || 0)),
    until: String(until || "").slice(0, 10),
    createdAt: createdAt || new Date().toISOString(),
    progress: {},
    goes: 0,
    oinksEarned: 0,
    lastGoAt: null,
    finishedAt: null,
  };
  if (status === "draft" || status === "archived") { list.status = status; list.until = status === "draft" ? "" : list.until; }
  if (actor && actor.id) { list.adultId = actor.id; if (actor.name) list.adultName = actor.name; }
  return list;
}

export function homeworkLists(lc) {
  const h = lc && lc.homework;
  return h && Array.isArray(h.lists) ? h.lists : [];
}

// LB-382 receive fixes (Alan, 1 Oct). The same list, for the duplicate check on a shared list:
// same type and the same SET of pairs, each side compared with normaliseAnswer (so case,
// accents and spacing do not make a list different), order and pair ids ignored. One word
// different is a different list.
// A spelling row's second side is its "as in" (5 Oct), not part of what makes it the same list.
function sameListKey(type, pairs) {
  const spelling = (type || "spelling") === "spelling";
  return `${type || "spelling"}\n${(pairs || []).map((p) => `${normaliseAnswer(p && p.a)}\u0001${spelling ? "" : normaliseAnswer(p && p.b)}`).sort().join("\n")}`;
}

// True when this child already has the list in ANY place — on the grid, a draft, or on the
// shelf. Shelf lists count on purpose: the parent can switch that copy back on rather than add
// a second one that takes another live slot and splits the child's progress.
export function childHasSameList(lc, type, pairs) {
  const key = sameListKey(type, pairs);
  return homeworkLists(lc).some((l) => l && sameListKey(l.type, l.pairs) === key);
}

export function findHomeworkList(lc, code) {
  return homeworkLists(lc).find((l) => l && l.code === code) || null;
}

// LB-377 (Alan, 27 Sep). A list is in one of three places, and only one of them is the child's
// grid. `status` carries the two that a date cannot express; a list without one behaves exactly
// as it always did, so every list already saved is on the grid or the shelf by its date alone.
//
//   draft     built, never handed over, no practise-until date at all. Up to HW_MAX_DRAFTS, and
//             OUTSIDE the live limit — the whole point is that a fourth list can be made.
//   (none)    the ordinary case: on the grid while its date holds, on the shelf once it passes.
//   archived  taken off the grid by hand, words and progress intact, until it is activated again.
export function isDraft(list) { return !!(list && list.status === "draft"); }
export function isArchived(list) { return !!(list && list.status === "archived"); }

// Live = on the child's grid: not a draft, not archived, and today is on or before its
// practise-until date. A list whose every word is known stays live until then so the child can
// keep practising.
export function isListLive(list, today) {
  if (isDraft(list) || isArchived(list)) return false;
  return !!(list && list.until && list.until >= today);
}

export function liveLists(lc, today) {
  return homeworkLists(lc).filter((l) => isListLive(l, today)).sort((a, b) => String(a.until).localeCompare(String(b.until)) || String(a.createdAt).localeCompare(String(b.createdAt)));
}

export function draftLists(lc) {
  return homeworkLists(lc).filter(isDraft).sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

// The shelf: everything off the grid that is not a draft — lists whose date has passed and lists
// archived by hand, together, newest first. Each one says which it is on the page.
export function shelfLists(lc, today) {
  return homeworkLists(lc)
    .filter((l) => !isDraft(l) && !isListLive(l, today))
    .sort((a, b) => String(b.archivedAt || b.until || "").localeCompare(String(a.archivedAt || a.until || "")));
}

// Kept: the page and the harness both still ask for the finished ones on their own.
export function finishedLists(lc, today) {
  return shelfLists(lc, today).filter((l) => !isArchived(l));
}

export function canGoLive(lc, today) {
  return liveLists(lc, today).length < HW_MAX_LIVE;
}

export function canAddDraft(lc) {
  return draftLists(lc).length < HW_MAX_DRAFTS;
}

// The gate on the CREATE button. It was canGoLive alone, and the flow re-checked at the end,
// so a parent built a whole list and was only then told to delete one (Alan, 27 Sep). A list
// can always be STARTED while either a grid slot or a draft slot is free; where it lands is
// decided when it is saved, not before it is typed.
export function canAddList(lc, today) {
  return canGoLive(lc, today) || canAddDraft(lc);
}

// Off the grid, keeping the words and the progress. Deleting a list to make room threw the
// child's work away with it, which is the wrong price for a free slot.
export function archiveList(list, today) {
  if (!list) return list;
  list.status = "archived";
  list.archivedAt = today;
  return list;
}

// Back onto the grid with a new practise-until date. A draft has no date at all — it is asked
// for one here, at the moment it goes live, so it can never go stale waiting on the page.
export function activateList(list, until) {
  if (!list) return list;
  list.status = undefined;
  delete list.status;
  list.archivedAt = null;
  list.until = String(until || "").slice(0, 10);
  return list;
}

// Housekeeping (Alan, 24 Sep: "we can't have it growing forever"): everything on the grid and
// every draft stays, and the last HW_SHELF_KEEP off the grid — finished or archived — stay with
// them; older ones go, along with their words. Called at every write.
export function pruneHomework(lc, today) {
  if (!lc || !lc.homework) return;
  const live = liveLists(lc, today);
  const drafts = draftLists(lc);
  const shelf = shelfLists(lc, today).slice(0, HW_SHELF_KEEP);
  lc.homework.lists = [...live, ...drafts, ...shelf];
}

// ---- progress --------------------------------------------------------------------------

export function pairProgress(list, pairId) {
  const p = list && list.progress && list.progress[pairId];
  return p && typeof p === "object" ? p : { days: [], knownAt: null, lastWrong: null, right: 0, wrong: 0 };
}

export function isPairKnown(list, pairId) {
  const p = pairProgress(list, pairId);
  return Array.isArray(p.days) && p.days.length >= HW_KNOWN_DAYS;
}

export function knownCount(list) {
  return (list && list.pairs ? list.pairs : []).filter((p) => isPairKnown(list, p.id)).length;
}

// How often a word has been answered right, and on how many days that counted (Alan, 29 Sep).
// `days` is the same list the two-days rule reads, so on a "both" list a day counts only when
// the word was right every way it was asked; `right` counts every right answer.
export function pairCounts(list, pairId) {
  const p = pairProgress(list, pairId);
  return { right: p.right || 0, days: Array.isArray(p.days) ? p.days.length : 0 };
}

export function pairState(list, pairId) {
  if (isPairKnown(list, pairId)) return "known";
  const p = pairProgress(list, pairId);
  return (p.right || 0) + (p.wrong || 0) > 0 ? "learning" : "notyet";
}

export function isListComplete(list) {
  return !!(list && list.pairs && list.pairs.length > 0 && knownCount(list) === list.pairs.length);
}

// ---- one go ----------------------------------------------------------------------------
//
// A go asks EVERY word on the list, in a shuffled order (Alan, 28 Sep: "a list of 20 spellings is
// only testing 10 words"; it used to stop at ten). A language list set to "both" asks every row
// both ways, so six rows are twelve questions: each row's first direction is picked at random,
// every row is asked once before any is asked the second time, and a row's two questions never
// sit side by side, so the first cannot hand the child the second. A word answered right today
// does not count a second day, so a child cannot make a list "known" in one sitting — that is
// the whole point of the two-days rule.
function shuffleWith(arr, rnd) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) { const j = rnd(i + 1); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

export function pickGo(list, today, { rnd = randomInt } = {}) {
  if (!list || !Array.isArray(list.pairs) || !list.pairs.length) return [];
  const pairs = shuffleWith(list.pairs, rnd);
  if (list.type !== "language" || list.direction !== "both") return pairs.map((p) => goItem(list, p, rnd));
  const other = (d) => (d === "en2lang" ? "lang2en" : "en2lang");
  const firstDir = new Map(pairs.map((p) => [p.id, rnd(2) === 0 ? "en2lang" : "lang2en"]));
  const firsts = pairs.map((p) => goItem(list, p, rnd, firstDir.get(p.id)));
  const seconds = shuffleWith(pairs, rnd).map((p) => goItem(list, p, rnd, other(firstDir.get(p.id))));
  if (seconds.length > 1 && seconds[0].pairId === firsts[firsts.length - 1].pairId) [seconds[0], seconds[1]] = [seconds[1], seconds[0]];
  return [...firsts, ...seconds];
}

// What the child is shown for one pair. Language lists honour the direction; for "both",
// pickGo says which way (`dir`), and without one a direction is picked at random. An English-vocabulary word is asked as a choice of
// meanings drawn from the list's other meanings (a sheet gives no distractors of its own).
export function goItem(list, pair, rnd = randomInt, dir = null) {
  // A spelling row's b is its "as in" (Alan, 5 Oct): shown above Listen and spoken after the word.
  if (list.type === "spelling") return { pairId: pair.id, mode: "spell", prompt: pair.a, answer: pair.a, hint: asInLine(pair.b) };
  if (list.type === "english") {
    const others = list.pairs.filter((p) => p.id !== pair.id && p.b).map((p) => p.b);
    const distractors = shuffleWith(others, rnd).slice(0, 3);
    return { pairId: pair.id, mode: "choose", prompt: pair.a, answer: pair.b, options: shuffleWith([pair.b, ...distractors], rnd) };
  }
  const way = list.direction === "both" ? (dir || (rnd(2) === 0 ? "en2lang" : "lang2en")) : list.direction;
  return way === "lang2en"
    ? { pairId: pair.id, mode: "type", dir: way, prompt: pair.b, answer: pair.a }
    : { pairId: pair.id, mode: "type", dir: way, prompt: pair.a, answer: pair.b };
}

export function markItem(item, given) {
  if (!item) return false;
  if (item.mode === "choose") return normaliseAnswer(given) === normaliseAnswer(item.answer);
  return answersMatchLoose(given, item.answer);
}

// Writes a finished go into the list. `results` = [{ pairId, correct }]. Returns what the
// caller needs to settle: whether the list became complete on THIS go.
// A "both" row comes back twice. Every answer counts toward right/wrong and Oinks, but the DAY
// counts toward "known" only when the row was right every way it was asked: a word is known
// when the child knows it both ways.
export function applyGo(list, results, today, nowIso, opts = {}) {
  if (!list) return { completedNow: false, known: 0, total: 0 };
  list.progress = list.progress && typeof list.progress === "object" ? list.progress : {};
  const wasComplete = isListComplete(list);
  let oinks = 0;
  const allRight = new Map();
  const asked = new Map();
  for (const r of results || []) if (r && r.pairId) {
    allRight.set(r.pairId, (allRight.has(r.pairId) ? allRight.get(r.pairId) : true) && !!r.correct);
    asked.set(r.pairId, (asked.get(r.pairId) || 0) + 1);
  }
  // A go left part way (Alan, 29 Sep) can have asked a "both" row one way only. Its right answer
  // counts; its day waits until the row has been right both ways in one go. A whole go asks
  // every row both ways, so this only ever bites on a stopped one.
  const oneWayOnly = (id) => !!opts.stopped && list.type === "language" && list.direction === "both" && (asked.get(id) || 0) < 2;
  const dayCounts = (id) => allRight.get(id) && !oneWayOnly(id);
  for (const r of results || []) {
    if (!r || !r.pairId || !list.pairs.some((p) => p.id === r.pairId)) continue;
    const p = list.progress[r.pairId] = { ...pairProgress(list, r.pairId) };
    p.days = Array.isArray(p.days) ? [...p.days] : [];
    if (r.correct) {
      p.right = (p.right || 0) + 1;
      if (dayCounts(r.pairId) && !p.days.includes(today)) p.days.push(today);
      if (p.days.length >= HW_KNOWN_DAYS && !p.knownAt) p.knownAt = today;
      if (list.oinksOn) oinks += 1;
    } else {
      p.wrong = (p.wrong || 0) + 1;
      p.lastWrong = today;
    }
  }
  list.goes = (list.goes || 0) + 1;
  list.lastGoAt = nowIso || new Date().toISOString();
  list.oinksEarned = (list.oinksEarned || 0) + oinks;
  const complete = isListComplete(list);
  const completedNow = complete && !wasComplete && !list.finishedAt;
  if (completedNow) list.finishedAt = list.lastGoAt;
  return { completedNow, known: knownCount(list), total: list.pairs.length, oinks };
}

// Edit words after creation: keeps the progress of a pair whose id survives, drops the rest.
export function replacePairs(list, pairs, makeId) {
  const mk = typeof makeId === "function" ? makeId : (i) => `${list.code}-${Date.now().toString(36)}-${i}`;
  const next = (pairs || []).slice(0, HW_MAX_PAIRS).map((p, i) => ({ id: p.id || mk(i), a: String(p.a || "").trim(), b: sideB(list.type, p.b) })).filter((p) => p.a);
  const keep = new Set(next.map((p) => p.id));
  list.pairs = next;
  list.progress = Object.fromEntries(Object.entries(list.progress || {}).filter(([id]) => keep.has(id)));
  if (list.finishedAt && !isListComplete(list)) list.finishedAt = null;
  return list;
}

// ---- the parent's numbers ---------------------------------------------------------------

export function homeworkStats(lc, today, weekStartOf) {
  const lists = homeworkLists(lc);
  const thisWeek = weekStartOf(today);
  const lastWeek = weekStartOf(new Date(new Date(`${thisWeek}T12:00:00`).getTime() - 7 * 86400000).toISOString().slice(0, 10));
  let learntThisWeek = 0; let learntLastWeek = 0; let goesThisWeek = 0;
  for (const l of lists) {
    for (const pr of Object.values(l.progress || {})) {
      if (!pr || !pr.knownAt) continue;
      const wk = weekStartOf(pr.knownAt);
      if (wk === thisWeek) learntThisWeek += 1;
      else if (wk === lastWeek) learntLastWeek += 1;
    }
    if (l.lastGoAt && weekStartOf(String(l.lastGoAt).slice(0, 10)) === thisWeek) goesThisWeek += Math.min(l.goes || 0, 1);
  }
  return { live: liveLists(lc, today).length, learntThisWeek, learntLastWeek, goesThisWeek };
}

// A spellings list also lands in the child's custom spelling list, unticked and tagged, so
// the words are there if Spelling is ever taken back from Coach (Alan, 25 Sep). Unticked on
// purpose: under LB-278 a ticked custom word takes over the manual spelling test, and a
// homework list must not change a test the parent did not set. Spellings only, for now.
export function addSpellingsToBank(lc, list, makeId) {
  if (!lc || !list || list.type !== "spelling") return 0;
  if (!Array.isArray(lc.spellingWords)) lc.spellingWords = [];
  const have = new Set(lc.spellingWords.map((w) => normaliseAnswer(w.word)));
  let added = 0;
  for (const p of list.pairs) {
    const key = normaliseAnswer(p.a);
    if (!key || have.has(key)) continue;
    have.add(key);
    const description = String(p.b || "").trim().slice(0, 160); // its "as in" travels with it (LB-292's field)
    lc.spellingWords.push({ id: makeId(), word: p.a, testInclude: false, source: "homework", homeworkCode: list.code, ...(description ? { description } : {}) });
    added += 1;
  }
  return added;
}

// A spelling list's words go to the shared word bank for a generated clip (TTS_SCOPE.md §5.3),
// the same as a word a parent adds to the custom spelling list (LearningAdminTab queueWordbank):
// bare strings, queued in household state inside the same update(), sent by App.jsx's flush a
// few seconds later with no household code anywhere near them, and voiced by the server straight
// away if they pass the rude-word filter. Until a clip exists the round uses the device voice.
// Spellings only: vocabulary is read, not heard, and the bank's voice is English. `only`, when
// given, limits it to those words. Saving a list (new or edited) queues all of them.
export function queueSpellingAudio(s, list, only = null) {
  if (!s || !list || list.type !== "spelling") return 0;
  const queued = new Set((s.pendingWordbankSubmissions || []).map((w) => String(w).trim()));
  const add = [];
  for (const p of list.pairs || []) {
    const w = String(p.a || "").trim();
    if (!w || (only && !only.has(w))) continue;
    if (!queued.has(w)) { queued.add(w); add.push(w); }
    // Its "as in" goes too (Alan, 5 Oct: "The as ins haven't run through TTS"), as a clip of its
    // own: the round plays the word, a pause, then this. The bank voices a phrase only when it
    // starts "as in" (worker/lib/tts-wordbank.js), which every line now does (asInLine).
    const h = asInLine(p.b); // a line saved before 6 Oct without "as in" is sent as it now reads
    if (/^as in\s\S/i.test(h) && h.length <= 160 && !queued.has(h)) { queued.add(h); add.push(h); }
  }
  if (add.length) s.pendingWordbankSubmissions = [...(s.pendingWordbankSubmissions || []), ...add];
  return add.length;
}

// The history row for one item, in the shape src/lib/testHistory.js keeps (display text only).
export function historyRow(item, given, correct) {
  return { q: item.mode === "spell" ? item.answer : item.prompt, given: given || "", answer: item.answer, correct: !!correct };
}
